import { memo, useMemo } from 'react'
import type { Divisao } from '../api'
import {
  blocosDoPodio,
  marcoPorExtenso,
  podioDasMarcas,
  porcentagemDaMeta,
  PASSO_DO_MARCO,
  POR_MARCA_NO_PODIO,
} from '../marcos'
import { porMarcaNoPodio } from '../projetor'
import { valorCurto } from '../formato'
import { Confetes } from './Confetes'
import { VelaJupiter } from './VelaJupiter'

/**
 * A comemoração de cada milhão do total geral.
 *
 * Tela cheia por 15 segundos, decidido em 13/10: o total em letras enormes, o
 * milhão batido por extenso, o primeiro representante de CADA marca com o valor
 * dele, e confete caindo por cima. A rotação fica pausada enquanto ela dura,
 * para ninguém perder a marca que estava na tela.
 *
 * A METADE da meta (R$ 14 mi) e a META (R$ 28 mi) usam a mesma tela, com três
 * diferenças: duram 20 s em vez de 12, jogam o dobro de confete e trocam o
 * `SEIS MILHÕES!` por `ESTAMOS NA METADE!` ou `META BATIDA!`, com o valor dito
 * por escrito. Elas acontecem uma vez cada, e o telão precisa dizer isso sem
 * ninguém explicar.
 *
 * Três decisões que o telão impôs:
 *
 *  1. **O confete é CSS, não canvas.** São 60 divs com `animation` e nenhum
 *     `requestAnimationFrame`: a animação roda na composição do navegador, fora
 *     da thread do JavaScript, e por isso ela não disputa com o SSE nem trava a
 *     chegada do snapshot seguinte. Num telão ligado há dias, laço de animação
 *     em JS é o tipo de coisa que degrada devagar e ninguém percebe.
 *  2. **Nada é consultado aqui.** O pódio sai do `painel` que a tela já tem
 *     (`topRepresentante` por divisão, que vem no `/api/painel`): a comemoração
 *     não pode depender de oito requisições no instante em que dispara.
 *  3. **O número grande é o TOTAL REAL, não o marco redondo.** Mostra-se
 *     `R$ 6.014 mil` — quanto a War Room vendeu neste instante —, e o milhão
 *     cruzado aparece por extenso logo abaixo (`SEIS MILHÕES!`). Foi pedido
 *     assim: o que a sala quer ver grande é o total, e o marco é a razão da
 *     festa. Se o total chegar `null` (ninguém mediu), cai no marco redondo,
 *     que é a única coisa que se sabe de certo naquele momento.
 */

/* 60 peças no milhão e 120 na metade e na meta. A geração saiu para
   `confete.ts` em 14/10 (a festa por marca usa 80 e 160, e a paleta dourada):
   a quantidade é parâmetro, e as peças seguem determinísticas — mesmo índice,
   mesma peça, em todo telão. */
const CONFETES_MILHAO = 60
const CONFETES_DOBRADOS = 120
/* A SUPER META joga o DOBRO da meta, e coloridos: foi o pedido de 16/10 — a
   maior festa do evento tem de parecer maior que a da meta, que ja era dourada
   e com o dobro do confete do milhao. */
const CONFETES_SUPER_META = 240

/* Seis velas atras do titulo: tres de cada lado, num LEQUE SIMETRICO.

   A primeira versao dava a cada vela um angulo, uma altura e um tamanho
   proprios, para a fileira nao parecer carimbo repetido — e o resultado ficou
   torto (16/10). O que organiza sem deixar monotono e a regularidade: as bases
   na MESMA linha, o angulo abrindo de dentro para fora em passo constante, e o
   tamanho diminuindo no mesmo passo. O lado direito e o espelho exato do
   esquerdo; o que muda entre as seis e so o jorro, pela `semente`. */
const ANGULO_INTERNO = 16
const PASSO_DO_LEQUE = 13
const VELAS_POR_LADO = 3

const VELAS_DA_SUPER_META = (['esquerda', 'direita'] as const).flatMap((lado, ladoIndice) =>
  Array.from({ length: VELAS_POR_LADO }, (_, i) => ({
    lado,
    /* `posicao` 0 e a de dentro, perto do texto; 2 e a de fora. */
    posicao: i,
    semente: ladoIndice * VELAS_POR_LADO + i,
    graus: ANGULO_INTERNO + i * PASSO_DO_LEQUE,
    escala: 1 - i * 0.12,
  })),
)

type Props = {
  /** `true` no telão da sala: só o campeão de cada marca, em letra grande. */
  projetor: boolean
  /** O marco batido: 6 para o sexto milhão. */
  marco: number
  /** O total geral AGORA, que é o número grande da tela. */
  total: number | null
  /** `true` quando o que caiu foi a META GERAL, e não só mais um milhão. */
  meta: boolean
  /** `true` quando o que caiu foi a METADE da meta. */
  metade: boolean
  /** A meta do cadastro, dita por escrito na comemoração dela. */
  metaGeral: number | null
  /**
   * O VALOR da super meta batida (30_000_000), ou `null`.
   *
   * Vence `meta` e `metade` na tela, como vence no gancho: cruzar R$ 30 mi é
   * também cruzar o 30º milhão, e a sala vê uma festa, não duas.
   */
  superMeta: number | null
  divisoes: Divisao[]
}

function ComemoracaoBase({
  projetor, marco, total, meta, metade, metaGeral, superMeta, divisoes,
}: Props) {
  const naSuperMeta = superMeta !== null
  /* No projetor, UMA linha por marca: as 24 do top 3 saem em 11 px num
     1024×768 (o piso do `clamp`), e 11 px no telão não se lê do fundo da sala. */
  const blocos = useMemo(
    () => blocosDoPodio(podioDasMarcas(divisoes, porMarcaNoPodio(projetor, POR_MARCA_NO_PODIO))),
    [divisoes, projetor],
  )

  return (
    /* `role="status"` e não `alert`: é uma comemoração, não um aviso de erro. */
    <div
      className={
        naSuperMeta
          ? 'comemoracao na-super-meta'
          : meta ? 'comemoracao na-meta' : metade ? 'comemoracao na-metade' : 'comemoracao'
      }
      role="status"
    >
      {/* DOURADO em todas, desde 14/10: o milhão usava a paleta colorida e a
          sala pediu a cor de festa em toda travessia. */}
      {/* DOURADO em todas, desde 14/10 — menos na SUPER META, que volta ao
          colorido a pedido (16/10): ela precisa se distinguir da festa da meta,
          e dourado em cima de dourado some. */}
      <Confetes
        quantidade={
          naSuperMeta ? CONFETES_SUPER_META : meta || metade ? CONFETES_DOBRADOS : CONFETES_MILHAO
        }
        paleta={naSuperMeta ? 'colorida' : 'dourada'}
      />
      <div className="comemoracao-miolo">
        <div className="comemoracao-rotulo">META VENDAS</div>
        <div className="comemoracao-valor">
          {typeof total === 'number' && Number.isFinite(total)
            ? valorCurto(total)
            : valorCurto(marco * PASSO_DO_MARCO)}
        </div>
        {naSuperMeta ? (
          <>
            {/* O TÍTULO com as velas atrás: elas sao `position: absolute` dentro
                deste bloco, entao ficam atras do texto sem empurrar o layout. */}
            <div className="comemoracao-extenso com-velas">
              {/* O NUCLEO tem a largura do texto, e e dele que as velas se
                  afastam: ancoradas na borda do BLOCO, a de dentro encostava no
                  "S" em telas estreitas e sobrava faixa vazia nas largas. */}
              <span className="super-meta-nucleo">
              {VELAS_DA_SUPER_META.map(vela => (
                <VelaJupiter
                  key={`${vela.lado}-${vela.semente}`}
                  lado={vela.lado}
                  semente={vela.semente}
                  grausDeInclinacao={vela.graus}
                  escala={vela.escala}
                  /* A posição no leque vai para o CSS: é ele que afasta cada
                     uma do texto, e um `data-` evita seis seletores
                     `nth-of-type` que quebram se a ordem do array mudar. */
                  posicaoNoLeque={vela.posicao}
                />
              ))}
              <span className="super-meta-texto">SUPER META</span>
              </span>
            </div>
            <div className="comemoracao-meta-dita">
              {valorCurto(superMeta)} · {porcentagemDaMeta(superMeta, metaGeral)} DA META
            </div>
          </>
        ) : meta ? (
          <>
            <div className="comemoracao-extenso">META BATIDA!</div>
            {/* A meta aparece por escrito: é ela que dá sentido ao "batida", e
                de longe a sala lê a frase antes de conferir o número grande. */}
            <div className="comemoracao-meta-dita">
              100% DE {valorCurto(metaGeral ?? marco * PASSO_DO_MARCO)}
            </div>
          </>
        ) : metade ? (
          <>
            <div className="comemoracao-extenso">ESTAMOS NA METADE!</div>
            <div className="comemoracao-meta-dita">
              50% DE {valorCurto(metaGeral ?? marco * PASSO_DO_MARCO * 2)}
            </div>
          </>
        ) : (
          <div className="comemoracao-extenso">{marcoPorExtenso(marco)}</div>
        )}

        {blocos.length === 0 ? (
          /* Pódio vazio tem de DIZER por que, e não sumir: em 13/10 a
             comemoração do sexto milhão saiu sem ele (a página havia carregado
             antes do primeiro ranking) e a tela não dava pista nenhuma de que
             faltava algo. */
          <div className="comemoracao-podio">
            <div className="comemoracao-podio-titulo">
              O RANKING DAS MARCAS ENTRA NO PRÓXIMO CICLO
            </div>
          </div>
        ) : (
          <div className="comemoracao-podio">
            <div className="comemoracao-podio-titulo">
              {projetor ? 'O CAMPEÃO DE CADA MARCA' : 'TOP 3 REPRESENTANTES DE CADA MARCA'}
            </div>
            {/* DUAS COLUNAS de blocos: 24 linhas numa coluna so davam 1.243 px e
                nao cabiam em 1.080 — medido, e projetor nao rola. */}
            <div className="comemoracao-colunas">
              {blocos.map(bloco => (
                <div className="podio-marca" key={bloco.id}>
                  <div className="podio-marca-nome">{bloco.marca}</div>
                  {bloco.linhas.map(linha => (
                    <div className="comemoracao-linha" key={linha.id}>
                      <span className="comemoracao-posicao">{linha.posicao}º</span>
                      <span className="comemoracao-nome" title={linha.nome}>{linha.nome}</span>
                      <span className="comemoracao-valor-linha">{valorCurto(linha.valor)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* `memo` pelo marco e pela identidade das divisões: o painel por baixo continua
   recebendo snapshot enquanto a comemoração está na tela, e sem isto as 60 peças
   de confete seriam reconciliadas a cada chegada — o que não as reinicia, mas
   gasta reconciliação de graça a cada segundo do relógio. */
export const Comemoracao = memo(
  ComemoracaoBase,
  (antes, depois) =>
    antes.projetor === depois.projetor
    && antes.marco === depois.marco
    && antes.total === depois.total
    && antes.meta === depois.meta
    && antes.metade === depois.metade
    && antes.superMeta === depois.superMeta
    && antes.metaGeral === depois.metaGeral
    && antes.divisoes === depois.divisoes,
)
